/**
 * Travel Compendium Modal System
 * Handles the 88ml + Travel Set offer modal for Ormonde Jayne
 *
 * IMPORTANT: 88ml and 4x10ml are VARIANTS of the same product.
 * The modal triggers when adding an 88ml VARIANT (not a separate product).
 */

(function() {
  'use strict';

  const TravelCompendium = {
    // DOM References
    modal: null,

    // State
    currentProductId: null,
    currentProductTitle: null,
    currentVariantId: null,        // The 88ml variant being added
    current88mlVariantId: null,    // The 88ml variant ID for this product
    selectedTravelSet: null,
    isProcessing: false,
    originalFormHandler: null,

    // Configuration
    STORAGE_KEY: 'oj_tc_declined',
    DISCOUNT_PERCENT: 40,

    /**
     * Initialize the Travel Compendium system
     */
    init: function() {
      // Check if Travel Compendium is enabled
      if (!window.travelCompendium || !window.travelCompendium.enabled) {
        console.log('Travel Compendium: Disabled or no travel sets available');
        return;
      }

      this.modal = document.getElementById('travel-compendium-modal');
      if (!this.modal) {
        console.warn('Travel Compendium: Modal element not found');
        return;
      }

      this.bindEvents();
      console.log('Travel Compendium: Initialized successfully');
    },

    /**
     * Bind modal event listeners
     */
    bindEvents: function() {
      // Close buttons and backdrop
      this.modal.querySelectorAll('[data-tc-close]').forEach(el => {
        el.addEventListener('click', (e) => {
          // Only close from backdrop on step 1
          if (e.target.classList.contains('tc-modal-backdrop')) {
            const step2 = this.modal.querySelector('[data-tc-step="2"]');
            if (step2 && step2.style.display !== 'none') {
              return; // Don't close on backdrop during selection
            }
          }
          this.close();
        });
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('open')) {
          this.close();
        }
      });

      // Step 1: Show selection
      const showSelectionBtn = this.modal.querySelector('[data-tc-action="show-selection"]');
      if (showSelectionBtn) {
        showSelectionBtn.addEventListener('click', () => this.showStep2());
      }

      // Step 1: Decline
      const declineBtn = this.modal.querySelector('[data-tc-action="decline"]');
      if (declineBtn) {
        declineBtn.addEventListener('click', () => this.decline());
      }

      // Step 2: Add both
      const addBothBtn = this.modal.querySelector('[data-tc-action="add-both"]');
      if (addBothBtn) {
        addBothBtn.addEventListener('click', () => this.addBothToCart());
      }

      // Step 2: Skip
      const skipBtn = this.modal.querySelector('[data-tc-action="skip"]');
      if (skipBtn) {
        skipBtn.addEventListener('click', () => this.decline());
      }
    },

    // Reference to the product form element that triggered the modal
    productFormElement: null,

    /**
     * Check if modal should be shown for this variant
     * @param {string} productId - The product ID
     * @param {string} variantId - The variant being added (should be 88ml)
     */
    shouldShowModal: function(productId, variantId) {
      // Check if already declined for this product in this session
      const declined = this.getDeclinedProducts();
      if (declined.includes(String(productId))) {
        return false;
      }

      // Check if Travel Sets are available
      if (!window.travelCompendium || !window.travelCompendium.travelSets || window.travelCompendium.travelSets.length === 0) {
        return false;
      }

      return true;
    },

    /**
     * Get list of declined product IDs from session storage
     */
    getDeclinedProducts: function() {
      try {
        const stored = sessionStorage.getItem(this.STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        return [];
      }
    },

    /**
     * Mark a product as declined for this session
     */
    setDeclined: function(productId) {
      const declined = this.getDeclinedProducts();
      if (!declined.includes(String(productId))) {
        declined.push(String(productId));
        try {
          sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(declined));
        } catch (e) {
          // Silent fail for private browsing
        }
      }
    },

    /**
     * Open the modal
     */
    open: function() {
      // Reset to step 1
      const step1 = this.modal.querySelector('[data-tc-step="1"]');
      const step2 = this.modal.querySelector('[data-tc-step="2"]');
      if (step1) step1.style.display = 'block';
      if (step2) step2.style.display = 'none';

      this.selectedTravelSet = null;
      this.resetSelection();

      // Populate travel sets grid
      this.populateTravelSets();

      // Show modal
      this.modal.classList.add('open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Hide Gorgias chat button
      this.hideGorgiasChat();

      // Focus first button for accessibility
      setTimeout(() => {
        const firstButton = this.modal.querySelector('.tc-modal__btn--primary');
        if (firstButton) firstButton.focus();
      }, 100);

      // Track event
      this.trackEvent('tc_modal_seen', {
        product_id: this.currentProductId,
        product_title: this.currentProductTitle,
        variant_id: this.currentVariantId
      });
    },

    /**
     * Close the modal
     */
    close: function() {
      this.modal.classList.remove('open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Show Gorgias chat button
      this.showGorgiasChat();
    },

    /**
     * Show step 2 (Travel Set selection)
     */
    showStep2: function() {
      const step1 = this.modal.querySelector('[data-tc-step="1"]');
      const step2 = this.modal.querySelector('[data-tc-step="2"]');

      if (step1) step1.style.display = 'none';
      if (step2) step2.style.display = 'block';

      // Preselect recommended if available
      this.preselectRecommended();

      // Track event
      this.trackEvent('tc_modal_accepted', {
        product_id: this.currentProductId,
        variant_id: this.currentVariantId
      });
    },

    /**
     * Populate the Travel Sets grid
     * Uses new data structure with productTitle, productId, variantId
     */
    populateTravelSets: function() {
      const grid = document.getElementById('tc-travel-sets-grid');
      if (!grid || !window.travelCompendium || !window.travelCompendium.travelSets) return;

      grid.innerHTML = '';

      window.travelCompendium.travelSets.forEach((set, index) => {
        const item = document.createElement('div');
        item.className = 'tc-modal__item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.setAttribute('tabindex', '0');
        item.dataset.variantId = set.variantId;
        item.dataset.productId = set.productId;
        item.dataset.title = set.productTitle;
        item.dataset.price = set.price;
        item.dataset.discountedPrice = set.discountedPrice;

        const formattedPrice = this.formatMoney(set.discountedPrice);
        const formattedOriginalPrice = this.formatMoney(set.price);

        // Use productTitle from new data structure
        item.innerHTML = `
          <img src="${set.image}" alt="${set.productTitle}" class="tc-modal__item-image" loading="lazy">
          <h4 class="tc-modal__item-title">${set.productTitle}</h4>
          <p class="tc-modal__item-price">
            ${formattedPrice}
            <s>${formattedOriginalPrice}</s>
          </p>
        `;

        // Click handler
        item.addEventListener('click', () => this.selectTravelSet(item, set));

        // Keyboard handler
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectTravelSet(item, set);
          }
        });

        grid.appendChild(item);
      });
    },

    /**
     * Preselect the recommended Travel Set if configured
     * Uses recommendedVariantId (the 4x10ml variant of the same product)
     */
    preselectRecommended: function() {
      if (window.travelCompendium && window.travelCompendium.recommendedVariantId) {
        const grid = document.getElementById('tc-travel-sets-grid');
        const recommended = grid.querySelector(`[data-variant-id="${window.travelCompendium.recommendedVariantId}"]`);

        if (recommended) {
          const setData = window.travelCompendium.travelSets.find(
            s => String(s.variantId) === String(window.travelCompendium.recommendedVariantId)
          );
          if (setData) {
            this.selectTravelSet(recommended, setData);
            // Scroll to selected item
            recommended.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }
    },

    /**
     * Handle Travel Set selection
     */
    selectTravelSet: function(element, setData) {
      const grid = document.getElementById('tc-travel-sets-grid');

      // Remove previous selection
      grid.querySelectorAll('.tc-modal__item').forEach(item => {
        item.classList.remove('selected');
        item.setAttribute('aria-selected', 'false');
      });

      // Add selection to clicked item
      element.classList.add('selected');
      element.setAttribute('aria-selected', 'true');
      this.selectedTravelSet = setData;

      // Update footer display - use productTitle from new data structure
      document.getElementById('tc-selected-name').textContent = setData.productTitle;
      document.getElementById('tc-price-original').textContent = this.formatMoney(setData.price);
      document.getElementById('tc-price-discounted').textContent = this.formatMoney(setData.discountedPrice);

      // Enable add button
      const addBtn = this.modal.querySelector('[data-tc-action="add-both"]');
      if (addBtn) addBtn.disabled = false;

      // Track event
      this.trackEvent('tc_travel_set_selected', {
        product_id: this.currentProductId,
        travel_set_product_id: setData.productId,
        travel_set_variant_id: setData.variantId,
        travel_set_title: setData.productTitle
      });
    },

    /**
     * Reset selection state
     */
    resetSelection: function() {
      const grid = document.getElementById('tc-travel-sets-grid');
      if (grid) {
        grid.querySelectorAll('.tc-modal__item').forEach(item => {
          item.classList.remove('selected');
          item.setAttribute('aria-selected', 'false');
        });
      }

      const selectedName = document.getElementById('tc-selected-name');
      const priceOriginal = document.getElementById('tc-price-original');
      const priceDiscounted = document.getElementById('tc-price-discounted');

      if (selectedName) selectedName.textContent = 'None';
      if (priceOriginal) priceOriginal.textContent = '';
      if (priceDiscounted) priceDiscounted.textContent = '';

      const addBtn = this.modal.querySelector('[data-tc-action="add-both"]');
      if (addBtn) addBtn.disabled = true;
    },

    /**
     * Handle decline - add just the 88ml
     */
    decline: function() {
      // Mark as declined for this session
      this.setDeclined(this.currentProductId);

      // Close modal first
      this.close();

      // Use the product form's method to add to cart (maintains all existing logic)
      if (this.productFormElement && typeof this.productFormElement.proceedWithAddToCart === 'function') {
        this.productFormElement.proceedWithAddToCart();
      } else {
        // Fallback: add directly
        this.addToCartDirect(this.currentVariantId, () => {
          this.openCartPopup();
        });
      }

      // Track event
      this.trackEvent('tc_modal_declined', {
        product_id: this.currentProductId,
        variant_id: this.currentVariantId
      });
    },

    /**
     * Add both 88ml and Travel Set to cart
     * IMPORTANT: Store the 88ml VARIANT ID for pairing (not product ID)
     */
    addBothToCart: function() {
      if (!this.selectedTravelSet || this.isProcessing) return;
      this.isProcessing = true;

      this.showLoader();

      // Calculate savings
      const savings = this.selectedTravelSet.price - this.selectedTravelSet.discountedPrice;

      // Add 88ml first
      this.addToCartDirect(this.currentVariantId, () => {
        // Then add Travel Set (4x10ml variant) with properties for tracking
        // Store the 88ml VARIANT ID for pairing
        const travelSetData = {
          id: this.selectedTravelSet.variantId,
          quantity: 1,
          properties: {
            '_travel_compendium': 'true',
            '_paired_with_88ml_variant': String(this.currentVariantId),
            '_paired_with_product': String(this.currentProductId),
            '_tc_discount_percent': String(this.DISCOUNT_PERCENT)
          }
        };

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [travelSetData] })
        })
        .then(response => response.json())
        .then(() => {
          this.hideLoader();
          this.isProcessing = false;
          this.close();
          this.openCartPopup();

          // Track completion
          this.trackEvent('tc_compendium_completed', {
            product_id: this.currentProductId,
            variant_88ml_id: this.currentVariantId,
            travel_set_product_id: this.selectedTravelSet.productId,
            travel_set_variant_id: this.selectedTravelSet.variantId,
            travel_set_title: this.selectedTravelSet.productTitle,
            savings: savings
          });
        })
        .catch(error => {
          console.error('Travel Compendium: Error adding Travel Set', error);
          this.hideLoader();
          this.isProcessing = false;
        });
      });
    },

    /**
     * Direct add to cart (bypassing interceptors)
     */
    addToCartDirect: function(variantId, callback) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }]
        })
      })
      .then(response => response.json())
      .then(() => {
        if (typeof callback === 'function') callback();
      })
      .catch(error => {
        console.error('Travel Compendium: Error adding to cart', error);
      });
    },

    /**
     * Open the cart popup/drawer
     */
    openCartPopup: function() {
      if (window.cart_popup && typeof window.cart_popup.updateCartPopup === 'function') {
        window.cart_popup.updateCartPopup(function() {
          if (typeof $ !== 'undefined') {
            $('.cart-popup-wrapper').addClass('open');
            $('html').addClass('cart-popup-wrapper-open');
          }
        });
      } else {
        // Fallback: trigger cart icon click
        const cartIcon = document.querySelector('.nav-bag-li a');
        if (cartIcon) cartIcon.click();
      }
    },

    /**
     * Show loading overlay
     */
    showLoader: function() {
      const loader = this.modal.querySelector('.tc-modal__loader');
      if (loader) {
        loader.style.display = 'flex';
        loader.setAttribute('aria-hidden', 'false');
      }
    },

    /**
     * Hide loading overlay
     */
    hideLoader: function() {
      const loader = this.modal.querySelector('.tc-modal__loader');
      if (loader) {
        loader.style.display = 'none';
        loader.setAttribute('aria-hidden', 'true');
      }
    },

    /**
     * Format money using Shopify's formatter
     */
    formatMoney: function(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, window.money_format || window.currency_symbol);
      }
      // Fallback formatting
      return (cents / 100).toFixed(2);
    },

    /**
     * Hide Gorgias chat button
     */
    hideGorgiasChat: function() {
      const chatButton = document.getElementById('chat-button');
      if (chatButton) {
        chatButton.style.display = 'none';
      }
    },

    /**
     * Show Gorgias chat button
     */
    showGorgiasChat: function() {
      const chatButton = document.getElementById('chat-button');
      if (chatButton) {
        chatButton.style.display = '';
      }
    },

    /**
     * Track analytics events
     */
    trackEvent: function(eventName, data) {
      // Google Analytics 4
      if (typeof gtag === 'function') {
        gtag('event', eventName, {
          event_category: 'Travel Compendium',
          ...data
        });
      }

      // Google Analytics Universal
      if (typeof ga === 'function') {
        ga('send', 'event', 'Travel Compendium', eventName, JSON.stringify(data));
      }

      // Dispatch custom event for other tracking systems
      document.dispatchEvent(new CustomEvent('travelCompendium:' + eventName, {
        detail: data
      }));

      // Console log for debugging (remove in production)
      console.log('Travel Compendium Event:', eventName, data);
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      TravelCompendium.init();
    });
  } else {
    TravelCompendium.init();
  }

  // Expose for external use
  window.TravelCompendium = TravelCompendium;
})();
